/**
 * Utility functions for date formatting and conversion
 */

/**
 * Formats a date to European date format (dd.mm.yyyy) without time
 */
export function formatDateToEuropean(isoDate: string | null | undefined): string {
  if (!isoDate) return '';

  try {
    const date = new Date(isoDate);
    if (isNaN(date.getTime())) return '';

    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();

    return `${day}.${month}.${year}`;
  } catch {
    return '';
  }
}

/**
 * Short date-time for availability hints: "14.10 о 17:22" – short enough to sit
 * beside a title on a phone. The year only when it is not the current one.
 * `at` is the word between date and time ("о" / "at").
 */
export function formatShortDateTime(isoDate: string, at: string): string {
  const d = new Date(isoDate);
  if (isNaN(d.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  const year = d.getFullYear() !== new Date().getFullYear() ? `.${d.getFullYear()}` : '';
  return `${pad(d.getDate())}.${pad(d.getMonth() + 1)}${year} ${at} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/**
 * Formats a date to European datetime format (dd.mm.yyyy HH:mm)
 */
export function formatDateTimeEuropean(isoDate: string | null | undefined): string {
  if (!isoDate) return '';

  try {
    const date = new Date(isoDate);
    if (isNaN(date.getTime())) return '';

    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');

    return `${day}.${month}.${year} ${hours}:${minutes}`;
  } catch {
    return '';
  }
}

// --- Timezone utilities ---

/**
 * Format a date in a specific timezone using Intl.DateTimeFormat
 */
export function formatDateInTimezone(
  isoDate: string | null | undefined,
  timezone: string,
  language: string,
  opts?: Intl.DateTimeFormatOptions
): string {
  if (!isoDate) return '';
  try {
    const date = new Date(isoDate);
    if (isNaN(date.getTime())) return '';
    const locale = language === 'uk' ? 'uk-UA' : 'en-US';
    return new Intl.DateTimeFormat(locale, { timeZone: timezone, ...opts }).format(date);
  } catch {
    return '';
  }
}

/**
 * Format time (HH:mm) in a specific timezone. Returns null for midnight (00:00).
 */
export function formatTimeInTimezone(isoDate: string | null | undefined, timezone: string): string | null {
  if (!isoDate) return null;
  try {
    const date = new Date(isoDate);
    if (isNaN(date.getTime())) return null;
    const parts = new Intl.DateTimeFormat('en-GB', {
      timeZone: timezone,
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    }).formatToParts(date);
    const h = parts.find(p => p.type === 'hour')?.value || '00';
    const m = parts.find(p => p.type === 'minute')?.value || '00';
    if (h === '00' && m === '00') return null;
    return `${h}:${m}`;
  } catch {
    return null;
  }
}

/**
 * Get timezone abbreviation like "EET", "EEST", "EST", etc.
 */
export function getTimezoneAbbreviation(timezone: string, date?: Date): string {
  try {
    const d = date || new Date();
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: timezone,
      timeZoneName: 'short',
    }).formatToParts(d);
    return parts.find(p => p.type === 'timeZoneName')?.value || timezone;
  } catch {
    return timezone;
  }
}

/** Both IANA spellings of the Ukrainian zone are in `content.timezone`. */
const KYIV_ZONES = new Set(['Europe/Kyiv', 'Europe/Kiev']);

/**
 * Human label naming the zone a printed event time belongs to, e.g.
 * "за київським часом (GMT+3)" / "Kyiv time (GMT+3)".
 *
 * An emailed start time with no zone attached is a trap: the reader assumes
 * their own clock. The offset is resolved against the event's own date so it is
 * DST-correct (Kyiv is GMT+3 in September, GMT+2 in December), and non-Kyiv
 * zones fall back to the city name so the label is never silently wrong.
 */
export function formatEventTimezoneLabel(
  timezone: string,
  language: string,
  atDate?: Date | string | null
): string {
  const isUk = language === 'uk';
  try {
    const reference = atDate ? new Date(atDate) : new Date();
    const date = isNaN(reference.getTime()) ? new Date() : reference;

    const offset = new Intl.DateTimeFormat('en-GB', {
      timeZone: timezone,
      timeZoneName: 'shortOffset',
    })
      .formatToParts(date)
      .find(p => p.type === 'timeZoneName')?.value || '';

    const zoneName = KYIV_ZONES.has(timezone)
      ? (isUk ? 'за київським часом' : 'Kyiv time')
      : timezone.split('/').pop()?.replace(/_/g, ' ') || timezone;

    return offset ? `${zoneName} (${offset})` : zoneName;
  } catch {
    return timezone;
  }
}

/**
 * Save path: Convert a browser-local Date to UTC as if the time was in the event timezone.
 *
 * Example: Organizer in NYC picks "10:00" → JS creates Date with 10:00 EST.
 * If event timezone is Europe/Kyiv, we want UTC to represent 10:00 EET, not 10:00 EST.
 * So we compute the offset difference and adjust.
 */
export function localDateToEventTimezoneISO(date: Date | null, eventTimezone: string): string | null {
  if (!date) return null;
  try {
    // Get the date/time components as they appear in the browser (what the user typed)
    const y = date.getFullYear();
    const mo = date.getMonth();
    const d = date.getDate();
    const h = date.getHours();
    const mi = date.getMinutes();

    // Build a formatter that shows time in the event timezone
    const formatter = new Intl.DateTimeFormat('en-US', {
      timeZone: eventTimezone,
      year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit', second: '2-digit',
      hour12: false,
    });

    // Find what UTC time produces the same wall-clock reading in the event timezone
    // Start with a rough guess: use the same components as UTC
    let guess = new Date(Date.UTC(y, mo, d, h, mi, 0, 0));

    // Iterate to converge (max 3 iterations)
    for (let i = 0; i < 3; i++) {
      const parts = formatter.formatToParts(guess);
      const gY = parseInt(parts.find(p => p.type === 'year')!.value);
      const gMo = parseInt(parts.find(p => p.type === 'month')!.value) - 1;
      const gD = parseInt(parts.find(p => p.type === 'day')!.value);
      const gH = parseInt(parts.find(p => p.type === 'hour')!.value);
      const gMi = parseInt(parts.find(p => p.type === 'minute')!.value);

      const diffMs =
        new Date(y, mo, d, h, mi).getTime() -
        new Date(gY, gMo, gD, gH, gMi).getTime();

      if (diffMs === 0) break;
      guess = new Date(guess.getTime() + diffMs);
    }

    return guess.toISOString();
  } catch {
    return date.toISOString();
  }
}

/**
 * Load path: Convert a UTC ISO string to a Date that, when displayed by the browser's
 * DateTimePicker (which uses browser TZ), shows the correct event-timezone time.
 *
 * Example: DB has "2025-03-15T08:00:00Z" (10:00 EET). If browser is in NYC (EST),
 * `new Date(iso)` would show 03:00. We want the picker to show 10:00,
 * so we create a Date whose local representation is 10:00.
 */
export function eventTimezoneISOToLocalDate(isoString: string | null, eventTimezone: string): Date | null {
  if (!isoString) return null;
  try {
    const utcDate = new Date(isoString);
    if (isNaN(utcDate.getTime())) return null;

    // Get the wall-clock time in the event timezone
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: eventTimezone,
      year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit', second: '2-digit',
      hour12: false,
    }).formatToParts(utcDate);

    const y = parseInt(parts.find(p => p.type === 'year')!.value);
    const mo = parseInt(parts.find(p => p.type === 'month')!.value) - 1;
    const d = parseInt(parts.find(p => p.type === 'day')!.value);
    const h = parseInt(parts.find(p => p.type === 'hour')!.value);
    const mi = parseInt(parts.find(p => p.type === 'minute')!.value);

    // Create a Date in the browser's local timezone with these components
    // so the DateTimePicker shows the event-timezone time
    return new Date(y, mo, d, h, mi);
  } catch {
    return new Date(isoString);
  }
}

/**
 * Check if the viewer's browser timezone differs from the event timezone.
 * Compares by UTC offset at the current moment (handles DST correctly).
 */
export function isViewerInDifferentTimezone(eventTimezone: string | null | undefined): boolean {
  if (!eventTimezone) return false;
  try {
    const browserTz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    if (browserTz === eventTimezone) return false;

    // Compare offsets – same offset means effectively same timezone for display
    const now = new Date();
    const browserOffset = getTimezoneOffsetMinutes(browserTz, now);
    const eventOffset = getTimezoneOffsetMinutes(eventTimezone, now);
    return browserOffset !== eventOffset;
  } catch {
    return false;
  }
}

/**
 * Get the UTC offset in minutes for a timezone at a given date.
 */
function getTimezoneOffsetMinutes(timezone: string, date: Date): number {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: timezone,
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', hour12: false,
  }).formatToParts(date);

  const y = parseInt(parts.find(p => p.type === 'year')!.value);
  const mo = parseInt(parts.find(p => p.type === 'month')!.value) - 1;
  const d = parseInt(parts.find(p => p.type === 'day')!.value);
  const h = parseInt(parts.find(p => p.type === 'hour')!.value);
  const mi = parseInt(parts.find(p => p.type === 'minute')!.value);

  // Wall-clock time in the timezone as if it were UTC
  const tzAsUtc = Date.UTC(y, mo, d, h, mi);
  // Actual UTC time
  const utc = date.getTime();
  return Math.round((tzAsUtc - utc) / 60000);
}

// --- Event date ranges ---

interface ZonedParts { y: number; m: number; d: number; h: number; mi: number }

function getZonedParts(date: Date, timezone: string): ZonedParts {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: timezone,
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', hour12: false,
  }).formatToParts(date);
  const get = (type: string) => parseInt(parts.find(p => p.type === type)!.value);
  // Some engines print midnight as "24" with hour12: false
  return { y: get('year'), m: get('month'), d: get('day'), h: get('hour') % 24, mi: get('minute') };
}

function parseIso(isoDate: string | null | undefined): Date | null {
  if (!isoDate) return null;
  const date = new Date(isoDate);
  return isNaN(date.getTime()) ? null : date;
}

const pad2 = (n: number) => String(n).padStart(2, '0');

/**
 * Compact numeric range for event cards, in the event's timezone. Shows only
 * what changes between the two days:
 * "29.10.2026", "29–30.10.2026", "30.10–02.11.2026", "30.12.2026–02.01.2027".
 */
export function formatEventDateRangeShort(
  startIso: string | null | undefined,
  endIso: string | null | undefined,
  timezone: string
): string {
  const start = parseIso(startIso);
  if (!start) return '';
  try {
    const s = getZonedParts(start, timezone);
    const end = parseIso(endIso);
    const e = end && end > start ? getZonedParts(end, timezone) : s;
    const dm = (p: ZonedParts) => `${pad2(p.d)}.${pad2(p.m)}`;

    if (s.y !== e.y) return `${dm(s)}.${s.y}–${dm(e)}.${e.y}`;
    if (s.m !== e.m) return `${dm(s)}–${dm(e)}.${e.y}`;
    if (s.d !== e.d) return `${pad2(s.d)}–${dm(e)}.${e.y}`;
    return `${dm(s)}.${s.y}`;
  } catch {
    return formatDateToEuropean(startIso);
  }
}

/** Short zone name printed next to event times: "Київ" / "Kyiv", else the IANA city. */
export function formatEventZoneName(timezone: string, language: string): string {
  if (KYIV_ZONES.has(timezone)) return language === 'uk' ? 'Київ' : 'Kyiv';
  return timezone.split('/').pop()?.replace(/_/g, ' ') || timezone;
}

/**
 * Worded range for the embed widget:
 *   "29 жовтня 2026, 14:00–18:00"
 *   "29 жовтня, 14:00 – 30 жовтня 2026, 18:00"
 *   "31 грудня 2026, 18:00 – 2 січня 2027, 12:00"
 *   "29–30 жовтня 2026" / "30 жовтня – 2 листопада 2026" (no time set)
 *
 * Whether a time is set (00:00 means "not set") is always judged in the
 * event's timezone; `displayTimezone` only changes which clock the result is
 * printed in, so the viewer's line never invents or drops a time.
 */
export function formatEventDateRangeLong(
  startIso: string | null | undefined,
  endIso: string | null | undefined,
  eventTimezone: string,
  language: string,
  options: { displayTimezone?: string; withTimes?: boolean } = {}
): { text: string; hasTime: boolean } {
  const start = parseIso(startIso);
  if (!start) return { text: '', hasTime: false };
  const displayTz = options.displayTimezone || eventTimezone;
  const withTimes = options.withTimes !== false;
  const isUk = language === 'uk';
  const locale = isUk ? 'uk-UA' : 'en-US';

  try {
    let end = parseIso(endIso);
    if (end && end <= start) end = null;

    const isMidnight = (date: Date) => {
      const p = getZonedParts(date, eventTimezone);
      return p.h === 0 && p.mi === 0;
    };
    const startHasTime = withTimes && !isMidnight(start);
    const endHasTime = withTimes && !!end && !isMidnight(end);

    const s = getZonedParts(start, displayTz);
    const e = end ? getZonedParts(end, displayTz) : null;
    const dayMonth = (date: Date) =>
      new Intl.DateTimeFormat(locale, { timeZone: displayTz, day: 'numeric', month: 'long' }).format(date);
    const withYear = (date: Date, p: ZonedParts) => `${dayMonth(date)}${isUk ? ' ' : ', '}${p.y}`;
    const time = (p: ZonedParts) => `${pad2(p.h)}:${pad2(p.mi)}`;
    const sameDay = !e || (s.y === e.y && s.m === e.m && s.d === e.d);

    if (sameDay) {
      let text = withYear(start, s);
      if (startHasTime) text += `, ${time(s)}${endHasTime && e ? `–${time(e)}` : ''}`;
      return { text, hasTime: startHasTime };
    }

    // Several days, from here on `end` and `e` are set
    const crossYear = s.y !== e!.y;
    if (startHasTime || endHasTime) {
      const from = (crossYear ? withYear(start, s) : dayMonth(start)) + (startHasTime ? `, ${time(s)}` : '');
      const to = withYear(end!, e!) + (endHasTime ? `, ${time(e!)}` : '');
      return { text: `${from} – ${to}`, hasTime: true };
    }

    if (crossYear) return { text: `${withYear(start, s)} – ${withYear(end!, e!)}`, hasTime: false };
    if (s.m !== e!.m) return { text: `${dayMonth(start)} – ${withYear(end!, e!)}`, hasTime: false };
    // Same month: "29–30 жовтня 2026" / "October 29–30, 2026"
    const month = new Intl.DateTimeFormat(locale, { timeZone: displayTz, month: 'long', day: 'numeric' })
      .formatToParts(end!)
      .find(p => p.type === 'month')!.value;
    const text = isUk ? `${s.d}–${e!.d} ${month} ${e!.y}` : `${month} ${s.d}–${e!.d}, ${e!.y}`;
    return { text, hasTime: false };
  } catch {
    return { text: formatDateToEuropean(startIso), hasTime: false };
  }
}

/**
 * The viewer's timezone when its UTC offset differs from the event's at the
 * event's own date (DST-correct), otherwise null.
 */
export function getDifferingViewerTimezone(eventTimezone: string, atIso?: string | null): string | null {
  try {
    const viewerTz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    if (!viewerTz || viewerTz === eventTimezone) return null;
    const at = parseIso(atIso) || new Date();
    return getTimezoneOffsetMinutes(viewerTz, at) !== getTimezoneOffsetMinutes(eventTimezone, at) ? viewerTz : null;
  } catch {
    return null;
  }
}
